
<script setup lang="ts">
import { AltPrimaryButton, PrimaryButton, LinkButton, LocaleToggle, scroll, WCEvents, useTranslation, hasTranslatedLink, applyHreflangTags, SPANISH_LOCALE } from '@hfc/ui-core';
import { ref, onBeforeMount, computed, inject, provide, onMounted } from 'vue';
import { PopoverGroup } from '@headlessui/vue';
import { CustomQuoteButtonConfig } from '@/page/domain/CustomQuoteButtonConfig';
import BrandingLogos from '@/shared/infrastructure/components/heading/BrandingLogos.vue';
import HeadingMobileMenu from '@/shared/infrastructure/components/heading/HeadingMobileMenu.vue';
import HeadingNavItem from '@/shared/infrastructure/components/heading/HeadingNavItem.vue';
import CallUsButton from '@/shared/infrastructure/components/heading/CallUsButton.vue';
import SearchBox from '@/shared/infrastructure/components/heading/SearchBox.vue';
import OverlayBackdrop from '@/shared/infrastructure/components/heading/OverlayBackdrop.vue';
import GetNewQuoteButton from '@/shared/infrastructure/components/heading/GetNewQuoteButton.vue';
import OepCountdownBanner from '@/shared/infrastructure/components/heading/OepCountdownBanner.vue';
import { MenuItem } from '@/shared/domain/MenuItem';
import { AppStore } from '@/shared/infrastructure/store/AppStore';
import { CarrierInterface } from '@/page/domain/CarrierInterface';
import { MenuPresets } from '@/shared/domain/MenuPresets';
import { useUrlHelper } from '@/page/infrastructure/composition/useUrlHelper';
import { twMerge } from 'tailwind-merge';

let appStore = inject<AppStore | null>('AppStore', null);
if (!appStore) {
  appStore = new AppStore();
  provide('AppStore', appStore);
}

const { getBaseUrl } = useUrlHelper();
const { emitVanillaJSCompatible } = WCEvents();
const { scrollValue } = scroll();
const { t } = useTranslation();

const props = defineProps<{
  title?: string,
  preset?: string,
  subTitle?: string,
  slotContent?: boolean,
  slotAfterHeader?: boolean,
  hideNavigation?: boolean,
  hideQuoteButton?: boolean,
  hideSearch?: boolean,
  phoneForCurrentRoute: string,
  emphasizedPhone?: string,
  forceSmallHeight?: boolean,
  customQuoteLink?: string,
  carrier?: string,
  showShopAndCompareOutlineButton?: boolean,
  hideBannerSubMessage?: boolean,
  headerContentClass?: string,
  forceSmallWidth?: boolean,
  localeQueryParamRedirection?: string,
}>();

const headingElement = ref();
const customLinkConfig: CustomQuoteButtonConfig = props.customQuoteLink ? JSON.parse(props.customQuoteLink) : {};
const getQuoteText = computed(() => customLinkConfig.label ?? t('common.shopAndCompare'));

const localeQueryParamRedirection = computed(() => props.localeQueryParamRedirection === 'true');

const carrier = computed(() => (props.carrier) ? JSON.parse(props.carrier) as CarrierInterface : null);
const isSpanish = computed(() => document.documentElement.lang === SPANISH_LOCALE);

const showLocaleToggle = computed(() => hasTranslatedLink());

const contentClass = computed(() => twMerge(
  'max-w-7xl mx-auto px-2 sm:px-6',
  props.forceSmallWidth && 'max-2xs:px-1',
  props.headerContentClass,
));

const isScrolling = computed(() => {
  return scrollValue.value > 120;
});

const headingHeightClass = computed(() => {
  const isSmall = isScrolling.value || props.forceSmallHeight;

  return {
    'h-[60px]': isSmall,
    'md:h-[95px]': isSmall,
    'h-[95px]': isSmall && props.forceSmallWidth,
    'h-[112px]': !isSmall,
  };
});

const showHomeButton = computed(() => props.preset === MenuPresets.MEDICARE);

const menuItems = ref([] as Array<MenuItem>);
const navItem = ref([]);
function closeAll (): void {
  const items: Array<typeof HeadingNavItem> = navItem.value;
  for (let i = 0; i < items.length; i++) {
    const element = items[i];
    element.onClickOutside();
  }
}

const triggerCustomEvent = (): void => {
  emitVanillaJSCompatible('shopAndCompare', headingElement.value, customLinkConfig);
};

onBeforeMount(async () => {
  const menuPreset = props.preset || '';
  const response = await appStore.getHeaderItems(menuPreset);
  if (response.success && response.data) {
    menuItems.value = response.data;
  }
});

onMounted(() => {
  applyHreflangTags();
});

defineExpose({
  closeAll
});
</script>
<template>
  <div>
    <div class="shadow-sm dark:shadow-none" ref="headingElement">
      <div class="sticky z-50 top-0 bg-white dark:bg-gradient-to-r dark:from-blue-900 dark:to-blue-500">
        <OepCountdownBanner />
        <div :class="contentClass">
          <div class="flex md:gap-4 justify-between md:justify-start heading md:py-0 transition-all duration-500" :class="headingHeightClass">
            <BrandingLogos :carrier-id="carrier?.codename" :force-small-width="forceSmallWidth" class="items-center" />
            <slot name="content">
              <PopoverGroup as="nav" class="hidden md:flex md:justify-end md:m-0 md:flex-1" v-if="!hideNavigation">
                <HeadingNavItem ref="navItem" v-for="menuItem in menuItems" :key="menuItem.label" :item="menuItem" :small-height="isScrolling || forceSmallHeight" :class="{ 'item-small-height': isScrolling || forceSmallHeight }" />
              </PopoverGroup>
            </slot>
            <div v-if="!hideSearch || phoneForCurrentRoute || !hideQuoteButton || showHomeButton || showLocaleToggle" class="flex flex-shrink-0 items-center justify-end flex-grow gap-3 overflow-visible mt-[-2px]" :class="{ 'md:flex-grow-0': !hideNavigation || slotContent }">
              <LocaleToggle v-if="showLocaleToggle" :query-param-redirection="localeQueryParamRedirection" />
              <OverlayBackdrop v-if="!hideSearch">
                <template v-slot:trigger>
                  <div class="flex">
                    <hfc-icon class="text-2xl hover:text-blue-300 dark:text-white text-blue flex w-6 h-6" icon="search" />
                  </div>
                </template>
                <template v-slot:content>
                  <SearchBox />
                </template>
              </OverlayBackdrop>
              <div class="hidden md:flex" v-if="showHomeButton">
                <LinkButton :href="getBaseUrl('/')">
                  <hfc-icon class="text-2xl hover:text-blue-300 dark:text-white text-blue flex w-7" icon="house" />
                </LinkButton>
              </div>
              <div class="flex flex-shrink-0 min-w-[20px] px-2" v-if="phoneForCurrentRoute && !isSpanish">
                <CallUsButton :phone-number="phoneForCurrentRoute" :show-phone-icon="!hideNavigation" :emphasized-phone="!!emphasizedPhone" :force-small-width="forceSmallWidth" />
              </div>
              <div class="hidden md:inline-block relative md:ml-3" v-if="!hideQuoteButton">
                <div class="flex flex-shrink-0">
                  <GetNewQuoteButton v-if="!customLinkConfig.modalEvent" :url="customLinkConfig.url" :target-blank="customLinkConfig.blank" :variant="showShopAndCompareOutlineButton" :outline="showShopAndCompareOutlineButton" class="w-full whitespace-nowrap text-base px-[30px]">
                    {{ getQuoteText }} <hfc-icon icon="arrow-right" class="ml-1 lg:ml-3 w-4 flex" />
                  </GetNewQuoteButton>
                  <AltPrimaryButton v-else-if="showShopAndCompareOutlineButton" @click="triggerCustomEvent" :outline="true" class="w-full whitespace-nowrap text-base px-[30px]">
                    {{ getQuoteText }} <hfc-icon icon="arrow-right" class="ml-1 lg:ml-3 w-4 flex" />
                  </AltPrimaryButton>
                  <PrimaryButton v-else @click="triggerCustomEvent" class="w-full whitespace-nowrap text-base px-[30px]">
                    {{ getQuoteText }} <hfc-icon icon="arrow-right" class="ml-1 lg:ml-3 w-4 flex" />
                  </PrimaryButton>
                </div>
                <div v-if="!hideBannerSubMessage" class="text-xs text-gray-500 dark:text-white absolute w-full text-center py-[5px] text-[13px]">{{ t('common.tagline') }}</div>
              </div>
            </div>
            <slot name="title">
              <div v-if="title || subTitle" class="flex items-end flex-grow flex-col">
                <h1 class="dark:text-white text-blue">{{ title }}</h1>
                <h2 class="dark:text-white text-blue">{{ subTitle }}</h2>
              </div>
            </slot>
            <HeadingMobileMenu :items="menuItems" v-if="!hideNavigation" />
          </div>
        </div>
        <div class="bg-blue-100 h-14 md:h-11 w-full" v-if="slotAfterHeader">
          <div class="max-w-7xl mx-auto px-4 sm:px-6">
            <slot name="after-header"></slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
